"use client";

import { Tool } from "@/types";
import Link from "next/link";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { getToolInstructions } from "@/lib/toolInstructions";
import { getRelatedTools } from "@/lib/seo";
import { usePathname } from "next/navigation";
import { getSiteContent } from "@/lib/site-content";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { getLocalizedTool, getLocalizedTools } from "@/lib/tool-localization";

interface ToolLayoutProps {
  tool: Tool;
  children: React.ReactNode;
}

export function ToolLayout({ tool, children }: ToolLayoutProps) {
  const relatedTools = getRelatedTools(tool, 3);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const localizedTool = getLocalizedTool(tool, locale);
  const localizedRelatedTools = getLocalizedTools(relatedTools, locale);
  const instructions = getToolInstructions(tool, locale);
  const content = getSiteContent(locale).toolsPage;

  return (
    <div className="container mx-auto py-8 md:py-12 px-4">
      <div className="mb-8 max-w-4xl">
        <Link
          href={withLocale(locale, "/tools")}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-positivus-dark hover:bg-positivus-green transition-colors text-sm font-medium"
        >
          <Icon icon={uiIcons.arrowLeft} className="h-4 w-4" />
          {content.backToTools}
        </Link>
        <h1 className="text-4xl font-medium md:text-5xl mt-2">{localizedTool.name}</h1>
        <p className="mt-4 text-xl text-gray-600 max-w-2xl">{localizedTool.description}</p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_350px]">
        {/* Main Content Area */}
        <div className="min-h-[400px] rounded-[30px] md:rounded-[45px] border border-positivus-dark bg-white p-5 md:p-12 shadow-[0_5px_0_0_#191A23]">
          {children}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="rounded-[45px] border border-positivus-dark bg-positivus-grey p-8">
            <h3 className="font-medium text-xl mb-4 flex items-center gap-2">
              {content.howToUse}
            </h3>
            <ol className="list-decimal list-inside space-y-3 text-base text-gray-700">
              {instructions.map((instruction, index) => (
                <li key={index} className="pl-2">{instruction}</li>
              ))}
            </ol>
          </div>

          <div className="rounded-[45px] border border-positivus-dark bg-positivus-dark text-white p-8">
            <h3 className="font-medium text-xl mb-2 text-positivus-green">{content.didYouKnow}</h3>
            <p className="text-white/90">{content.didYouKnowBody}</p>
          </div>

          {localizedRelatedTools.length > 0 && (
            <div className="rounded-[45px] border border-positivus-dark bg-white p-8">
              <h3 className="mb-4 text-xl font-medium">{content.relatedTools}</h3>
              <div className="space-y-4">
                {localizedRelatedTools.map((relatedTool) => (
                  <Link
                    key={relatedTool.id}
                    href={withLocale(locale, `/${relatedTool.slug}`)}
                    className="block rounded-[20px] border border-black/10 p-4 transition-colors hover:bg-positivus-green/15"
                  >
                    <p className="font-medium text-black">{relatedTool.name}</p>
                    <p className="mt-1 text-sm text-gray-600">{relatedTool.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
